// Network status + retry helpers.
//
// Capacitor's @capacitor/network plugin ships an addListener API that works
// on the web (via online/offline events), Android, and iOS. We wrap it in a
// React hook so screens can render different UI when the device is offline.
//
// retryWithBackoff() is for mutations and Edge Function calls. Reads are
// already retried by react-query; wrapping them here would double-retry.

import { useEffect, useSyncExternalStore } from "react";
import { Network } from "@capacitor/network";
import type { ConnectionStatus } from "@capacitor/network";
import { mapError } from "./errors";

// ---- online / offline store ---------------------------------------------

let currentStatus: ConnectionStatus = {
  connected: typeof navigator !== "undefined" ? navigator.onLine : true,
  connectionType: "unknown",
};

const listeners = new Set<() => void>();
let bootstrapped = false;
let pluginListenerHandle: { remove: () => void | Promise<void> } | null = null;

const notify = () => {
  for (const fn of listeners) fn();
};

const bootstrap = async () => {
  if (bootstrapped) return;
  bootstrapped = true;
  try {
    currentStatus = await Network.getStatus();
    notify();
    pluginListenerHandle = await Network.addListener("networkStatusChange", (status) => {
      currentStatus = status;
      notify();
    });
  } catch {
    // Plugin missing in plain web context (e.g. tests) — fall back to
    // browser's online/offline events.
    if (typeof window !== "undefined") {
      const update = () =>
        ((currentStatus = { connected: navigator.onLine, connectionType: "unknown" }), notify());
      window.addEventListener("online", update);
      window.addEventListener("offline", update);
    }
  }
};

const subscribe = (fn: () => void) => {
  void bootstrap();
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && pluginListenerHandle) {
      // Don't actually tear down the plugin listener — other consumers may
      // mount later. Cheaper to leave it open for the lifetime of the app.
    }
  };
};

const getSnapshot = (): ConnectionStatus => currentStatus;

/**
 * React hook returning the current network status. Re-renders on
 * connect/disconnect.
 */
export function useNetworkStatus(): ConnectionStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Imperative read — useful inside event handlers where a hook isn't an option. */
export function getCurrentNetworkStatus(): ConnectionStatus {
  void bootstrap();
  return currentStatus;
}

/** True if we currently believe the device is online. */
export function isOnline(): boolean {
  return getCurrentNetworkStatus().connected;
}

// ---- retry with backoff -------------------------------------------------

interface RetryOptions {
  /** Total attempts including the first. Default: 3 (1 + 2 retries). */
  maxAttempts?: number;
  /** Base delay in ms; subsequent attempts double it. Default: 1000. */
  baseDelayMs?: number;
  /**
   * If true, only retry when `mapError(err).retryable === true`. Default true —
   * we don't want to re-fire a "permission denied" indefinitely.
   */
  onlyOnRetryable?: boolean;
  /** Callback invoked with (attempt, delayMs) before each delay. */
  onRetry?: (attempt: number, delayMs: number) => void;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Run `fn`. If it throws and the error is retryable, retry with exponential
 * backoff. The defaults (3 attempts, 1s/2s base) match the prompt for Phase B.
 *
 * Usage:
 *   const { data, error } = await retryWithBackoff(() =>
 *     supabase.functions.invoke('account-deletion', { body: {} })
 *   );
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? 3;
  const baseDelayMs = options.baseDelayMs ?? 1000;
  const onlyOnRetryable = options.onlyOnRetryable ?? true;

  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === maxAttempts) break;
      if (onlyOnRetryable && !mapError(err).retryable) break;
      const delay = baseDelayMs * Math.pow(2, attempt - 1);
      options.onRetry?.(attempt, delay);
      // If we're offline, don't burn through retries hammering — wait for
      // network to come back, up to the delay we'd have slept anyway.
      if (!isOnline()) {
        await waitForOnline(delay);
      } else {
        await sleep(delay);
      }
    }
  }
  throw lastErr;
}

/**
 * Resolve when the device comes back online OR after `timeoutMs`, whichever
 * is first. Used by retryWithBackoff to avoid retrying while the radio is off.
 */
function waitForOnline(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    if (isOnline()) {
      resolve();
      return;
    }
    let cleared = false;
    const timer = setTimeout(() => {
      if (cleared) return;
      cleared = true;
      unsubscribe();
      resolve();
    }, timeoutMs);
    const unsubscribe = subscribe(() => {
      if (!isOnline() || cleared) return;
      cleared = true;
      clearTimeout(timer);
      unsubscribe();
      resolve();
    });
  });
}

// ---- pretty "X minutes ago" helper for the stale-data badge -------------

export function formatRelativeTime(timestampMs: number): string {
  const diffMs = Date.now() - timestampMs;
  if (diffMs < 60_000) return "just now";
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/** Threshold for "show the stale-data badge" — 1 hour per Phase B spec. */
export const STALE_DATA_THRESHOLD_MS = 60 * 60 * 1000;

// Kick off the bootstrap so getCurrentNetworkStatus() returns something
// useful before any hook mounts.
void bootstrap();
